import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FullWidthGridColumn, GridRow } from 'components/StaticGrid'
import { useForm, Controller } from 'react-hook-form'
import PropTypes from 'prop-types'
import { Tonality } from '@dsny/dsny-component-library'
import { useTheme } from 'styled-components'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { yupResolver } from '@hookform/resolvers/yup'
import { stepOneSchema } from './schema'
import {
  Container,
  FormWrapper,
  FullWidthButton,
  Title,
  StyledCheckbox,
  CheckboxLabel,
  SignUpLink
} from '../../components/AuthForm.styles'
import {
  Error,
  SignupHalfColumn,
  StyledPhoneInput,
  StyledTextField
} from './Steps.styles'
import { SIGN_UP_WORK_IN_OPTIONS } from 'utils/constants'
import SelectInput from 'components/SelectInput'
import PasswordValidation from '../../components/PasswordValidation'

const StepOne = ({ onSubmit, token = '', decodedToken }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMobileMediaQuery()
  const [displayTips, setDisplayTips] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [hasAcceptedTerms, setAcceptTerms] = useState(false)
  const [isPasswordValid, setIsPasswordValid] = useState(false)

  const {
    watch,
    control,
    setValue,
    handleSubmit,
    trigger,
    register,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(stepOneSchema),
    defaultValues: { token, email: token && decodedToken?.data?.email }
  })

  const handleCheckBoxChange = () => {
    setValue('hasAcceptedTerms', !hasAcceptedTerms)
    setAcceptTerms(!hasAcceptedTerms)
  }

  const industries = useMemo(
    () =>
      SIGN_UP_WORK_IN_OPTIONS.map((el) => ({
        value: el.value,
        label: t(`signup.${el.label}`)
      })),
    []
  )

  const onIndustryChange = (industry) => {
    setValue('industry', industry)
    trigger('industry')
  }

  const handlePasswordValidation = (isValid) => {
    setIsPasswordValid(isValid)
    if (isValid) {
      setDisplayTips(false)
    }
  }

  const watchPassword = watch('password', '')

  useEffect(() => {
    if (watchPassword) {
      setDisplayTips(true)
    }
  }, [watchPassword])

  return (
    <Container
      style={{ padding: 0, overflowY: 'auto', alignItems: 'flex-start' }}
    >
      <FormWrapper style={{ overflowX: 'hidden', padding: 4 }}>
        <Title style={{ marginBottom: token ? 8 : 32 }}>
          {!token
            ? t('signup.signup-header-title')
            : t('signup.tell-us-about-yourself')}
        </Title>

        {token && (
          <Tonality.UIText
            textStyle="body"
            color={theme.colors.neutralW70}
            style={{ fontFamily: 16, marginBottom: 32 }}
          >
            {t('signup.tell-us-about-yourself-body')}
          </Tonality.UIText>
        )}

        <form
          data-cy="signup-step-one-form"
          onSubmit={handleSubmit((values, e) => onSubmit(values, e))}
        >
          <GridRow gutterWidth={16} style={{ marginBottom: 24 }}>
            <SignupHalfColumn style={{ marginBottom: isMobile ? 24 : 0 }}>
              <div data-cy="signup-first-name">
                <StyledTextField
                  id="first_name"
                  name="first_name"
                  required
                  {...register('first_name')}
                  ref={null}
                  inputRef={register('first_name').ref}
                  labelText={t('signup.first-name')}
                  error={!!errors.first_name && t('signup.first-name-error')}
                />
              </div>
            </SignupHalfColumn>

            <SignupHalfColumn>
              <div data-cy="signup-last-name">
                <StyledTextField
                  id="last_name"
                  name="last_name"
                  required
                  {...register('last_name')}
                  ref={null}
                  inputRef={register('last_name').ref}
                  labelText={t('signup.last-name')}
                  error={!!errors.last_name && t('signup.last-name-error')}
                />
              </div>
            </SignupHalfColumn>
          </GridRow>

          <GridRow style={{ marginBottom: 24 }}>
            <FullWidthGridColumn>
              <div data-cy="signup-email">
                <StyledTextField
                  id="email"
                  name="email"
                  required
                  {...register('email')}
                  ref={null}
                  inputRef={register('email').ref}
                  labelText={t('signup.email')}
                  error={!!errors.email && t('signup.email-error')}
                  highlightColor={theme.colors.interact70}
                  borderColor={theme.colors.neutralW50}
                  readOnly={!!token}
                />
              </div>
            </FullWidthGridColumn>
          </GridRow>

          <div style={{ marginBottom: 24 }}>
            <Controller
              name="industry"
              control={control}
              render={({ field }) => (
                <SelectInput
                  dataCy="signup-industry"
                  required
                  label={`${t('signup.me-work-in')}...`}
                  selectOptions={industries}
                  value={field.value}
                  onChangeValue={(val) => {
                    field.onChange(val)
                    onIndustryChange?.(val)
                  }}
                  error={!!errors.industry}
                />
              )}
            />
            {!!errors.industry && <Error>{t('signup.industry-error')}</Error>}
          </div>

          <GridRow gutterWidth={16}>
            <SignupHalfColumn style={{ marginBottom: isMobile ? 24 : 0 }}>
              <div data-cy="signup-role">
                <StyledTextField
                  id="role"
                  name="role"
                  required
                  {...register('role')}
                  ref={null}
                  inputRef={register('role').ref}
                  labelText={t('signup.your-role')}
                  error={!!errors.role && t('signup.role-error')}
                />
              </div>
            </SignupHalfColumn>

            <SignupHalfColumn>
              <div data-cy="signup-phone">
                <StyledPhoneInput
                  id="phone"
                  name="phone"
                  {...register('phone')}
                  ref={null}
                  inputRef={register('phone').ref}
                  labelText={t('signup.company-phone-number')}
                  required
                  error={!!errors.phone && t('signup.phone-error')}
                />
              </div>
            </SignupHalfColumn>
          </GridRow>

          {token && (
            <>
              <GridRow style={{ marginTop: 12 }}>
                <FullWidthGridColumn>
                  <div data-cy="signup-password">
                    <StyledTextField
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      {...register('password')}
                      required
                      ref={null}
                      inputRef={register('password').ref}
                      onFocus={() => setDisplayTips(true)}
                      onClick={() => {
                        setShowPassword((prev) => !prev)
                      }}
                      labelText={t('login.password')}
                      error={!!errors.password && t('signup.password-error')}
                      tailingIcon={
                        showPassword ? 'Visibility' : 'VisibilityOff'
                      }
                      isTailingClickable
                      tailingStyle={{
                        paddingTop: 4,
                        paddingRight: '10px',
                        fill: theme.colors.neutralW100
                      }}
                    />
                  </div>
                </FullWidthGridColumn>
              </GridRow>

              <PasswordValidation
                displayTips={displayTips}
                watchPassword={watchPassword}
                onValidation={handlePasswordValidation}
              />

              <GridRow style={{ marginBottom: 32, marginTop: 24 }}>
                <FullWidthGridColumn>
                  <div data-cy="signup-confirm-password">
                    <StyledTextField
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirm_password"
                      {...register('confirm_password')}
                      required
                      ref={null}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      inputRef={register('confirm_password').ref}
                      labelText={t('signup.confirm-password')}
                      error={errors.confirm_password?.message}
                      tailingIcon={
                        showConfirmPassword ? 'Visibility' : 'VisibilityOff'
                      }
                      isTailingClickable
                      tailingStyle={{
                        paddingTop: 4,
                        paddingRight: '10px',
                        fill: theme.colors.neutralW100
                      }}
                    />
                  </div>
                </FullWidthGridColumn>
              </GridRow>
            </>
          )}

          <input
            id="token"
            name="token"
            type="hidden"
            value={token}
            ref={null}
            inputRef={register('token').ref}
            {...register('token')}
          />

          <GridRow
            data-cy="signup-terms"
            style={{
              marginBottom: 24,
              flexDirection: isMobile ? 'column' : 'row'
            }}
          >
            <FullWidthGridColumn
              style={{ paddingLeft: 18, display: 'flex', alignItems: 'center' }}
            >
              <StyledCheckbox
                data-cy="signup-terms-checkbox"
                id="hasAcceptedTerms"
                name="hasAcceptedTerms"
                {...register('hasAcceptedTerms')}
                checked={hasAcceptedTerms}
                onClick={handleCheckBoxChange}
                style={{
                  marginRight: 3,
                  background: hasAcceptedTerms
                    ? theme.colors.interact70
                    : theme.colors.neutralW20
                }}
              />
              <CheckboxLabel htmlFor="hasAcceptedTerms">
                {t('signup.me-accept')}
              </CheckboxLabel>

              <SignUpLink style={{ fontWeight: '400' }}>
                {t('signup.terms-and-conditions')}
              </SignUpLink>
            </FullWidthGridColumn>
          </GridRow>

          <FullWidthButton
            data-cy="signup-step-one-submit"
            type="submit"
            color="primary"
            setting="player"
          >
            {t('signup.proceed-to-next-step')}
          </FullWidthButton>
        </form>
      </FormWrapper>
    </Container>
  )
}

StepOne.propTypes = {
  isSignInFailed: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired
}

export default StepOne
